import React, { useState, useCallback } from 'react';
import Popover from 'material-ui/Popover';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';

export const DATA_FILL_FIELDS = [
    { key: 'gear', label: 'Gear' },
    { key: 'grab', label: 'Grab' },
    // { key: 'pumps', label: 'Pumps (Ballast / Cargo)' },
    { key: 'pumpsBallast', label: 'Pumps (Ballast)' },
    { key: 'pumpsCargo', label: 'Pumps (Cargo)' },
    { key: 'capacityGrain', label: 'Capacity (Grain)' },
    { key: 'capacityBale', label: 'Capacity (Bale)' },
    { key: 'capacityTankerSegregation', label: 'Tanker Capacity (Segregation)' },
    { key: 'capacityTankerCargoCapacity', label: 'Tanker Capacity (Cargo Capacity)' },
    { key: 'holdsHatches', label: 'Holds & Hatches' },
    { key: 'speedConsumption', label: 'Speed & Consumption (Laden)' },
    { key: 'owner', label: 'Owner' },
];

const emptyState = () =>
    DATA_FILL_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: null }), {});

const styles = {
    container: {
        padding: '12px 16px',
        width: '320px',
        fontSize: '12px',
        lineHeight: '1.5',
    },
    title: {
        fontWeight: 'bold',
        marginBottom: '10px',
        fontSize: '13px',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '6px',
    },
    label: {
        flex: 1,
        fontSize: '12px',
    },
    toggleGroup: {
        display: 'flex',
        gap: '4px',
    },
    toggleBtn: {
        width: '28px',
        height: '28px',
        minWidth: '28px',
        borderRadius: '4px',
        border: '1px solid #ddd',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
        backgroundColor: '#fff',
        transition: 'all 0.15s ease',
    },
    divider: {
        borderTop: '1px solid #eee',
        margin: '10px 0',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '6px',
        alignItems: 'center',
    },
};

const presentStyle = {
    ...styles.toggleBtn,
    backgroundColor: '#E8F5E9',
    borderColor: '#81C784',
    color: '#2E7D32',
};

const absentStyle = {
    ...styles.toggleBtn,
    backgroundColor: '#FFEBEE',
    borderColor: '#E57373',
    color: '#C62828',
};

const inactiveStyle = {
    ...styles.toggleBtn,
    borderColor: '#ddd',
    backgroundColor: '#fff',
    color: 'inherit',
};

export default function DataFillRatioFilter({ dataFillRatio, handleFilters }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [local, setLocal] = useState(emptyState);

    const open = !!anchorEl;

    const handleOpen = useCallback((e) => {
        setAnchorEl(e.currentTarget);
        setLocal({ ...emptyState(), ...dataFillRatio });
    }, [dataFillRatio]);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleToggle = useCallback((key, value) => {
        setLocal((prev) => ({
            ...prev,
            [key]: prev[key] === value ? null : value,
        }));
    }, []);

    const handleClear = useCallback(() => {
        setLocal(emptyState());
    }, []);

    const handleCheckAll = useCallback((value) => {
        setLocal((prev) => {
            const allMatch = Object.values(prev).every(v => v === value);
            return allMatch
                ? emptyState()
                : DATA_FILL_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: value }), {});
        });
    }, []);

    const handleSubmit = useCallback(() => {
        // send all non-null values (0 = absent, 1 = present)
        const active = Object.entries(local).reduce((acc, [k, v]) => {
            if (v != null) acc[k] = v;
            return acc;
        }, {});

        handleFilters('dataFillRatio', Object.keys(active).length > 0 ? active : null);
        setAnchorEl(null);
    }, [local, handleFilters]);

    // show indicator when filter is active
    const hasActiveFilter = dataFillRatio && Object.values(dataFillRatio).length;

    return (
        <React.Fragment>
            <RaisedButton
                label={`Data Fill Filter ${hasActiveFilter ? '●' : ''}`}
                onClick={handleOpen}
                style={{
                    height: 28,
                    lineHeight: '28px',
                }}
                labelStyle={{
                    fontSize: '12px',
                    textTransform: 'none',
                    padding: '0 12px',
                }}
                primary={!!hasActiveFilter}
            />
            <Popover
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                targetOrigin={{ horizontal: 'left', vertical: 'top' }}
                onRequestClose={handleClose}
            >
                <div style={styles.container}>
                    <div style={styles.title}>Filter by Data Fill Ratio</div>

                    {(() => {
                        const vals = Object.values(local);
                        const allPresent = vals.length > 0 && vals.every(v => v === 1);
                        const allAbsent = vals.length > 0 && vals.every(v => v === 0);
                        return (
                            <div style={{ ...styles.row, marginBottom: '10px' }}>
                                <span style={{ ...styles.label, fontWeight: 'bold', fontSize: '11px', color: '#888' }}>Select all</span>
                                <div style={styles.toggleGroup}>
                                    <button
                                        type="button"
                                        style={allPresent ? presentStyle : inactiveStyle}
                                        onClick={() => handleCheckAll(1)}
                                        title="Check all present"
                                    >
                                        ✓
                                    </button>
                                    <button
                                        type="button"
                                        style={allAbsent ? absentStyle : inactiveStyle}
                                        onClick={() => handleCheckAll(0)}
                                        title="Check all not present"
                                    >
                                        ✗
                                    </button>
                                </div>
                            </div>
                        );
                    })()}

                    {DATA_FILL_FIELDS.map((field) => (
                        <div key={field.key} style={styles.row}>
                            <span style={styles.label}>{field.label}</span>
                            <div style={styles.toggleGroup}>
                                <button
                                    type="button"
                                    style={local[field.key] === 1 ? presentStyle : inactiveStyle}
                                    onClick={() => handleToggle(field.key, 1)}
                                    title="Present"
                                >
                                    ✓
                                </button>
                                <button
                                    type="button"
                                    style={local[field.key] === 0 ? absentStyle : inactiveStyle}
                                    onClick={() => handleToggle(field.key, 0)}
                                    title="Not present"
                                >
                                    ✗
                                </button>
                            </div>
                        </div>
                    ))}

                    <div style={styles.divider} />

                    <div style={styles.footer}>
                        <FlatButton
                            label="Clear"
                            onClick={handleClear}
                            style={{ minWidth: 60 }}
                            labelStyle={{ fontSize: '12px', textTransform: 'none' }}
                        />
                        <FlatButton
                            label="Close"
                            onClick={handleClose}
                            style={{ minWidth: 60 }}
                            labelStyle={{ fontSize: '12px', textTransform: 'none' }}
                        />
                        <RaisedButton
                            label="Submit"
                            primary
                            onClick={handleSubmit}
                            style={{ minWidth: 60, height: 28, lineHeight: '28px' }}
                            labelStyle={{ fontSize: '12px', textTransform: 'none', padding: '0 12px' }}
                        />
                    </div>
                </div>
            </Popover>
        </React.Fragment>
    );
}



// WEBPACK FOOTER //
// ./app/components/Vessels/List/DataFillRatioFilter.jsx